import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const RECOMMENDATIONS_LIMIT = 8;
const UPSELL_SOURCE_TAG = 'DBX ATC Upsell';

class CrossSellModalComponent extends DialogComponent {
  #abortController = new AbortController();
  #cache = new Map();

  connectedCallback() {
    super.connectedCallback();

    document.querySelectorAll('cart-drawer-component[auto-open]').forEach((drawer) => {
      drawer.removeAttribute('auto-open');
    });

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd, {
      signal: this.#abortController.signal,
    });

    document.addEventListener('click', this.#handlePrefetchTrigger, {
      capture: true,
      signal: this.#abortController.signal,
    });
    document.addEventListener('pointerover', this.#handlePrefetchTrigger, {
      signal: this.#abortController.signal,
    });
    document.addEventListener('focusin', this.#handlePrefetchTrigger, {
      signal: this.#abortController.signal,
    });
    document.addEventListener('touchstart', this.#handlePrefetchTrigger, {
      passive: true,
      signal: this.#abortController.signal,
    });

    // Nasłuchiwanie kliknięcia w kartę produktu i zapis do sessionStorage
    this.addEventListener('click', this.#handleProductNavigation, {
      signal: this.#abortController.signal,
    });

    this.#bindActionButtons();
    this.#prefetchCurrentProduct();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #bindActionButtons() {
    const cartBtn = this.querySelector('[data-cross-sell-cart-btn]');
    const continueBtn = this.querySelector('[data-cross-sell-continue-btn]');

    cartBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      this.closeDialog();

      const drawer = document.querySelector('cart-drawer-component');
      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url;
      }
    });

    continueBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      this.closeDialog();
    });
  }

  /**
   * Zapisuje zamiar zakupu produktu z rekomendacji do sessionStorage przed nawigacją na PDP
   * @param {MouseEvent} event
   */
  #handleProductNavigation = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const link = target.closest('a[href*="/products/"]');
    
    // Ignorujemy kliknięcia poza linkami oraz w przycisk szybkiego dodania do koszyka
    if (!link || target.closest('[data-variant-id]')) return;

    const card = link.closest('[data-product-id]');
    const productId = card?.dataset?.productId;
    if (!productId) return;

    try {
      const payload = {
        productId: String(productId),
        tag: UPSELL_SOURCE_TAG,
        timestamp: Date.now(),
      };
      sessionStorage.setItem('pending_upsell_product', JSON.stringify(payload));
    } catch (err) {
      console.warn('CrossSellModal: Nie udało się zapisać do sessionStorage', err);
    }
  };

  formatMoney(cents) {
    return (Number(cents) / 100).toLocaleString('pl-PL', { minimumFractionDigits: 2 }) + ' zł';
  }

  #resizeImageUrl(url, width) {
    if (!url) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}`;
  }

  #prefetchCurrentProduct() {
    const canonical = document.querySelector('link[rel="canonical"]')?.href;
    const productIdMeta = document.querySelector('meta[property="product:retailer_item_id"]')?.content;
    if (!canonical || !productIdMeta) return;

    const match = canonical.match(/\/products\/([^/?#]+)/);
    if (!match) return;

    this.#prefetch(productIdMeta, match[1]);
  }

  /** @param {PointerEvent | FocusEvent | MouseEvent | TouchEvent} event */
  #handlePrefetchTrigger = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const card = target.closest?.('[data-product-id]');
    if (!card) return;

    const productId = String(card.dataset.productId);
    if (!productId || this.#cache.has(productId)) return;

    const link = card.querySelector('a[href*="/products/"]');
    const handleMatch = link?.getAttribute('href')?.match(/\/products\/([^/?#]+)/);
    if (!handleMatch) return;

    this.#prefetch(productId, handleMatch[1]);
  };

  #prefetch(productId, handle) {
    const key = String(productId);
    if (this.#cache.has(key)) return this.#cache.get(key);

    const promise = (async () => {
      const items = await this.#buildRecommendationList(key, handle);
      const htmlFragments = await Promise.all(items.map((item) => this.#fetchCardHtml(item.handle)));
      return htmlFragments.filter(Boolean).join('');
    })();

    this.#cache.set(key, promise);
    return promise;
  }

  /** @param {CartAddEvent} event */
  #handleCartAdd = (event) => {
    document.querySelectorAll('cart-drawer-component[auto-open]').forEach((drawer) => {
      drawer.removeAttribute('auto-open');
    });

    if (event.detail?.data?.didError) return;
    if (this.refs.dialog?.open) return;

    const productId = event.detail?.data?.productId;
    if (!productId) return;

    const key = String(productId);
    const { grid, addedProduct } = this.refs;
    addedProduct.innerHTML = '';
    grid.innerHTML = this.#cache.has(key) ? '' : '';

    this.showDialog();
    this.#populate(key);
  };

  async #populate(productId) {
    const { addedProduct } = this.refs;
    const key = String(productId);

    const recommendationsPromise = this.#renderRecommendations(key);

    const cart = await this.#fetchCart();
    const item = this.#findCartItem(cart, key);

    if (item) {
      this.#renderAddedProduct(addedProduct, item);
    }

    await recommendationsPromise;
    if (!this.#cache.has(key)) {
      await this.#renderRecommendations(key, item?.handle, cart);
    }
  }

  async #fetchCart() {
    try {
      const res = await fetch('/cart.js');
      return await res.json();
    } catch (err) {
      console.error('CrossSellModal: fetch cart error', err);
      return null;
    }
  }

  #findCartItem(cart, productId) {
    if (!cart?.items) return null;
    return (
      cart.items.find((i) => String(i.product_id) === String(productId)) || cart.items[cart.items.length - 1]
    );
  }

  #renderAddedProduct(container, item) {
    const imageUrl = this.#resizeImageUrl(item.image, 160);

    container.innerHTML = `
      <div class="cross-sell-modal__added-item">
        <img src="${imageUrl}" width="80" height="80" alt="${item.product_title}" loading="lazy">
        <div>
          <p class="cross-sell-modal__added-title">${item.product_title}</p>
          ${item.variant_title ? `<p class="cross-sell-modal__added-variant">${item.variant_title}</p>` : ''}
          <p class="cross-sell-modal__added-price">${this.formatMoney(item.final_price)}</p>
        </div>
      </div>`;
  }

  async #fetchComplementary(handle) {
    if (!handle) return [];
    try {
      const res = await fetch(`/products/${handle}?section_id=cross-sell-data`);
      if (!res.ok) return [];
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const script = doc.querySelector('[data-cross-sell-json]');
      if (!script) return [];
      const data = JSON.parse(script.textContent);
      return Array.isArray(data.complementary) ? data.complementary : [];
    } catch (err) {
      console.error('CrossSellModal: complementary fetch error', err);
      return [];
    }
  }

  async #fetchRecommendationsApi(productId, limit) {
    try {
      const res = await fetch(`/recommendations/products.json?product_id=${productId}&intent=related&limit=${limit}`);
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data.products) ? data.products : [];
    } catch (err) {
      console.error('CrossSellModal: recommendations API error', err);
      return [];
    }
  }

  #normalize(product) {
    return { id: product.id, handle: product.handle, available: product.available };
  }

  async #buildRecommendationList(productId, handle, cart) {
    const limit = RECOMMENDATIONS_LIMIT;
    const resolvedCart = cart || (await this.#fetchCart());

    const seen = new Set([String(productId)]);
    (resolvedCart?.items || []).forEach((cartItem) => seen.add(String(cartItem.product_id)));

    const result = [];

    const complementary = await this.#fetchComplementary(handle);
    for (const item of complementary) {
      if (result.length >= limit) break;
      const key = String(item.id);
      if (seen.has(key) || !item.available) continue;
      seen.add(key);
      result.push(this.#normalize(item));
    }

    if (result.length < limit) {
      const missing = limit - result.length;
      const apiProducts = await this.#fetchRecommendationsApi(productId, missing + complementary.length + 4);
      for (const item of apiProducts) {
        if (result.length >= limit) break;
        const key = String(item.id);
        if (seen.has(key) || !item.available) continue;
        seen.add(key);
        result.push(this.#normalize(item));
      }
    }

    return result.slice(0, limit);
  }

  async #fetchCardHtml(handle) {
    try {
      const res = await fetch(`/products/${handle}?section_id=cross-sell-card-single`);
      if (!res.ok) return '';
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const wrapper = doc.querySelector('[data-cross-sell-card-content]');
      return wrapper ? wrapper.innerHTML : '';
    } catch (err) {
      console.error('CrossSellModal: card fetch error', err);
      return '';
    }
  }

  async #renderRecommendationsProgressive(productId, handle, cart) {
    const { grid, recommendationsWrapper } = this.refs;
    const key = String(productId);

    grid.innerHTML = '';

    const items = await this.#buildRecommendationList(key, handle, cart);
    if (!items.length) {
      recommendationsWrapper.hidden = true;
      this.#cache.set(key, Promise.resolve(''));
      return;
    }

    recommendationsWrapper.hidden = false;

    const results = new Array(items.length).fill('');
    const wrappers = items.map(() => {
      const el = document.createElement('div');
      el.className = 'cross-sell-card-slot';
      el.hidden = true;
      grid.appendChild(el);
      return el;
    });

    await Promise.all(
      items.map(async (item, index) => {
        const html = await this.#fetchCardHtml(item.handle);
        if (!html) {
          wrappers[index].remove();
          return;
        }
        wrappers[index].innerHTML = html;
        wrappers[index].hidden = false;
        results[index] = html;
      })
    );

    const finalHtml = results.filter(Boolean).join('');
    this.#cache.set(key, Promise.resolve(finalHtml));

    if (!finalHtml) {
      recommendationsWrapper.hidden = true;
    }
  }

  async #renderRecommendations(productId, handle, cart) {
    const { grid, recommendationsWrapper } = this.refs;
    const key = String(productId);

    if (this.#cache.has(key)) {
      const html = await this.#cache.get(key);
      recommendationsWrapper.hidden = !html;
      grid.innerHTML = html || '';
      return;
    }

    if (handle === undefined) return;

    await this.#renderRecommendationsProgressive(key, handle, cart);
  }

  /** @param {MouseEvent & {target: HTMLElement}} event */
  async addToCart(event) {
    const btn = event.target.closest('[data-variant-id]');
    if (!btn) return;
    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    btn.disabled = true;
    btn.textContent = 'Dodawanie…';

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: { __upsell_id: UPSELL_SOURCE_TAG },
            },
          ],
        }),
      });

      if (!res.ok) throw new Error('Add to cart failed');
      await res.json();

      const cart = await this.#fetchCart();

      document.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'cross-sell-modal-component',
          itemCount: 1,
          productId: btn.closest('[data-product-id]')?.dataset.productId,
        })
      );

      btn.textContent = 'Dodano ✓';
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = 'Dodaj do koszyka';
      }, 1500);
    } catch (err) {
      console.error('CrossSellModal: add to cart error', err);
      btn.disabled = false;
      btn.textContent = 'Błąd — spróbuj ponownie';
    }
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cross-sell-modal-component')) {
  customElements.define('cross-sell-modal-component', CrossSellModalComponent);
}